import React, { useContext } from 'react';
import { AuthContext } from 'contexts';
import { useHistory } from 'react-router';
import { useAnsweringServiceDetails } from 'hooks';
import { SupraSubscriptionProductBillingModel } from '@bb/domain/dist/types';
import useOrgBillingModel from 'hooks/useOrgBillingModel';
import useOverduePaymentStage from 'hooks/useOverduePaymentStage';
import PaymentBanner from './PaymentBanner';

/**
 * The banner to show when the logged-in user has an outstanding payment due.
 * The content of the banner changes depending on if the logged-in user is
 * responsible for paying the outstanding balance or not.
 */
const OutstandingPaymentBanner: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const history = useHistory();

  const isGlobalAdmin = auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN;

  const { answeringServicePayer } = useAnsweringServiceDetails({
    viewingLevel: isGlobalAdmin ? 'org' : 'user',
    userId: auth.id,
  });
  const { supraSubscriptionPayer } = useOrgBillingModel();
  const { isUserSuspended, isOrgSuspended } = useOverduePaymentStage();

  // Determines if the logged-in user is responsible for paying for their own plan.
  const isUserPaying =
    answeringServicePayer === 'user' ||
    supraSubscriptionPayer === SupraSubscriptionProductBillingModel.Keyholder;

  // Determines if the org/brokerage is responsible for paying for the logged-in user's plan.
  // The supra subscription does not need to be considered in this check since org/brokerage is not an
  // option for who can pay.
  const isOrgPaying = answeringServicePayer === 'brokerage';

  const goToPayNowModalUser = () => {
    history.push(`/subscriptions_payments/user?payNow=true`);
  };
  const goToPayNowModalOrg = () => {
    history.push(`/subscriptions_payments/org?payNow=true`);
  };

  if (!isUserSuspended && !isOrgSuspended) return null;

  return (
    <>
      {isUserPaying && isUserSuspended && (
        <PaymentBanner
          message="Your service(s) have been suspended due to an outstanding payment."
          showPayNowButton
          goToPayNowModal={goToPayNowModalUser}
        />
      )}
      {isOrgPaying && isOrgSuspended && !isGlobalAdmin && (
        <PaymentBanner
          message="Your service(s) have been suspended. Please contact your brokerage for more information."
          showPayNowButton={false}
        />
      )}
      {isOrgPaying && isOrgSuspended && isGlobalAdmin && (
        <PaymentBanner
          message="Your brokerage service(s) have been suspended due to an outstanding payment. Please update your brokerage's card to resume service."
          showPayNowButton
          goToPayNowModal={goToPayNowModalOrg}
        />
      )}
    </>
  );
};

export default OutstandingPaymentBanner;
